import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatLKR, formatPct, formatDate } from "@/lib/api";
import { Plus, Pencil, Trash2, ArrowUpRight, ArrowDownRight, Coins, Wallet, TrendingUp, TrendingDown, Sparkles, X, Undo2, Building2, AlertTriangle, ArrowRight, Target, Activity, FileText, LayoutGrid, Table as TableIcon } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import HoldingDialog from "./HoldingDialog";
import SellHoldingDialog from "./SellHoldingDialog";
import MonthlyInvestmentDialog from "./MonthlyInvestmentDialog";
import ImportCsvDialog from "./ImportCsvDialog";
import { apiDelete, apiPost, apiGet } from "@/lib/api";
import { toast } from "sonner";
import KpiTile from "@/components/dashboard/KpiTile";
import HelpTooltip from "@/components/common/HelpTooltip";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import SignalBadge, { ScoreText } from "@/components/common/SignalBadge";
import ValuationBadge from "@/components/common/ValuationBadge";
import DataConfidenceDot from "@/components/common/DataConfidenceDot";
import SectorVerdictPill from "@/components/common/SectorVerdictPill";
import { Link } from "react-router-dom";
import ReturnProjectionPanel from "./ReturnProjectionPanel";
import ExpectedVsActualCard from "./ExpectedVsActualCard";
import PortfolioHealthTab from "./PortfolioHealthTab";

const PIE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

const PortfolioDashboard = ({ summary, onChange }) => {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [sellHolding, setSellHolding] = useState(null);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Phase 4 (Item 17) — when user navigates back from Stock Detail with
  // ?openMonthly=1 in the URL, automatically reopen the Monthly Investment
  // dialog so they land back where they were.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("openMonthly") === "1") {
        setMonthlyOpen(true);
        // Strip the param so it doesn't keep re-opening on refresh
        params.delete("openMonthly");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    } catch (_e) { /* ignore */ }
  }, []);
  // Medium #14 — Holdings view toggle. Table is default; preference persisted
  // in localStorage so the user only switches once.
  const [holdingsView, setHoldingsView] = useState(() => {
    try {
      const v = localStorage.getItem("cse:holdings-view");
      return v === "cards" ? "cards" : "table";
    } catch {
      return "table";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("cse:holdings-view", holdingsView); } catch { /* ignore */ }
  }, [holdingsView]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromWatchlistRef = React.useRef(null);
  const t = summary.totals;
  const holdings = summary.holdings || [];

  // Watchlist deep-link: /portfolio?add=COMB.X0000 opens the add-holding dialog
  // pre-filled with that security.
  useEffect(() => {
    const addSym = searchParams.get("add");
    if (!addSym) return;
    const findSec = async () => {
      try {
        const secs = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/securities`)).json();
        const sec = secs.find((s) => s.cse_symbol === addSym);
        if (sec) {
          fromWatchlistRef.current = addSym;
          setPrefill({
            company_code: sec.company_code,
            cse_symbol: sec.cse_symbol,
            share_type: sec.share_type,
          });
          setEditing(null);
          setDlgOpen(true);
        }
      } catch {
        /* noop */
      } finally {
        // Strip ?add= from the URL so it doesn't re-fire on refresh
        const next = new URLSearchParams(searchParams);
        next.delete("add");
        setSearchParams(next, { replace: true });
      }
    };
    findSec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const suggestion = summary.portfolio?.ai_suggestion;
  const dismissed = summary.portfolio?.dismissed_suggestions || [];
  // Stocks Claude suggested that haven't been added yet AND haven't been dismissed
  const heldSymbols = new Set(holdings.map((h) => h.cse_symbol));
  const dismissedSet = new Set(dismissed);
  const remainingSuggestions = (suggestion?.allocations || []).filter(
    (a) => !heldSymbols.has(a.cse_symbol) && !dismissedSet.has(a.cse_symbol),
  );
  const portfolioId = summary.portfolio?.portfolio_id;

  const onDismissSuggestion = async (a) => {
    if (!portfolioId) return;
    try {
      await apiPost(`/portfolios/${portfolioId}/dismiss-suggestion`, { cse_symbol: a.cse_symbol });
      toast.success(`Removed ${a.cse_symbol} from suggestions`);
      await onChange();
    } catch (e) {
      toast.error("Failed to dismiss suggestion");
    }
  };

  const onRestoreAllSuggestions = async () => {
    if (!portfolioId) return;
    try {
      await apiPost(`/portfolios/${portfolioId}/restore-suggestion`, {});
      toast.success("Suggestions restored");
      await onChange();
    } catch (e) {
      toast.error("Failed to restore suggestions");
    }
  };

  const closeAndRefresh = async () => {
    setDlgOpen(false);
    setEditing(null);
    setPrefill(null);
    await onChange();
  };

  const onAdd = () => { setEditing(null); setPrefill(null); setDlgOpen(true); };
  const onAddFromSuggestion = (a) => {
    setEditing(null);
    // Phase 9 Fix 1 — Pre-fill EVERYTHING from the suggestion so the user
    // sees company name + ticker + suggested shares + suggested price the
    // moment the dialog opens. They can still override the price with
    // their actual broker fill price.
    setPrefill({
      cse_symbol: a.cse_symbol,
      company_code: a.company_code,
      company_name: a.company_name,
      sector: a.sector,
      share_type: a.share_type,
      shares_purchased: a.shares,
      purchase_price: a.close_price ?? a.current_price ?? null,
      _from_suggestion: true,
    });
    setDlgOpen(true);
  };
  const onEdit = (h) => { setEditing(h); setPrefill(null); setDlgOpen(true); };
  const onDelete = (h) => {
    // Phase 7 Stage 8 — open the sell-tracking dialog instead of a
    // synchronous confirm() so we can capture the exit reason.
    setSellHolding(h);
  };
  const onSync = async () => {
    try {
      const res = await apiPost(`/portfolios/${summary.portfolio.portfolio_id}/sync-dividends`);
      toast.success(`Synced ${res.new_receipts} new dividend receipts`);
      await onChange();
    } catch (e) {
      toast.error("Sync failed");
    }
  };

  const sectorData = (summary.sector_breakdown || []).map((s, i) => ({
    name: s.sector,
    value: s.allocation_pct,
    valueLkr: s.value,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const totalGainPositive = (t.gain_loss_lkr || 0) >= 0;

  // Phase 7 Stage 8 — portfolio health KPI (header strip)
  const [healthHeader, setHealthHeader] = useState(null);
  useEffect(() => {
    if (!summary?.portfolio?.portfolio_id || holdings.length === 0) {
      setHealthHeader(null);
      return;
    }
    let cancelled = false;
    apiGet(`/portfolios/${summary.portfolio.portfolio_id}/health`)
      .then((res) => {
        if (!cancelled) setHealthHeader(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.portfolio?.portfolio_id, holdings.length]);

  return (
    <div className="space-y-6" data-testid="portfolio-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label={<span className="inline-flex items-center">Total invested<HelpTooltip termId="brokerage-commission" size="xs" /></span>}
          value={formatLKR(t.invested_amount_inc_brokerage || t.invested_amount, { compact: true, decimals: 2 })}
          icon={Wallet}
          testId="port-kpi-invested"
          helpText={
            t.total_brokerage_paid
              ? `incl. ${formatLKR(t.total_brokerage_paid, { compact: true, decimals: 0 })} brokerage`
              : null
          }
        />
        <KpiTile
          label="Current value"
          value={formatLKR(t.current_value, { compact: true, decimals: 2 })}
          icon={TrendingUp}
          testId="port-kpi-current"
        />
        <KpiTile
          label={<span className="inline-flex items-center">Unrealised P/L<HelpTooltip termId="unrealised-gain-loss" size="xs" /></span>}
          value={`${totalGainPositive ? "+" : ""}${formatLKR(t.gain_loss_lkr, { compact: true, decimals: 2 })}`}
          delta={t.gain_loss_pct}
          icon={totalGainPositive ? ArrowUpRight : ArrowDownRight}
          testId="port-kpi-pl"
          accent
          helpText={t.total_brokerage_paid ? "after buy brokerage" : null}
        />
        <KpiTile
          label={<span className="inline-flex items-center">Dividends received<HelpTooltip termId="dividend-wht" size="xs" /></span>}
          value={formatLKR(t.dividend_income_lkr, { compact: true, decimals: 2 })}
          helpText={
            t.dividend_income_lkr_gross
              ? `Net of 10% WHT · ${(t.yield_on_cost_pct || 0).toFixed(2)}% YoC`
              : `${(t.yield_on_cost_pct || 0).toFixed(2)}% Yield on Cost`
          }
          icon={Coins}
          testId="port-kpi-divs"
        />
      </div>

      {healthHeader && holdings.length > 0 && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm ${
            healthHeader.health_colour === "good"
              ? "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5"
              : healthHeader.health_colour === "fair"
              ? "border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5"
              : "border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5"
          }`}
          data-testid="portfolio-health-strip"
        >
          <div className="flex items-center gap-2">
            <Activity
              className={`h-4 w-4 ${
                healthHeader.health_colour === "good"
                  ? "text-[hsl(var(--success))]"
                  : healthHeader.health_colour === "fair"
                  ? "text-[hsl(var(--warning))]"
                  : "text-[hsl(var(--destructive))]"
              }`}
            />
            <span className="font-display font-semibold">
              Portfolio Health: {healthHeader.health_score}/100
            </span>
            <span className="text-muted-foreground">·</span>
            <span
              className={`font-medium ${
                healthHeader.health_colour === "good"
                  ? "text-[hsl(var(--success))]"
                  : healthHeader.health_colour === "fair"
                  ? "text-[hsl(var(--warning))]"
                  : "text-[hsl(var(--destructive))]"
              }`}
            >
              {healthHeader.health_label}
            </span>
            {healthHeader.exit_signals?.strong > 0 && (
              <Badge
                variant="outline"
                className="ml-2 text-[10px] bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30"
                data-testid="health-strip-strong-count"
              >
                🔴 {healthHeader.exit_signals.strong} exit signal{healthHeader.exit_signals.strong === 1 ? "" : "s"}
              </Badge>
            )}
            {healthHeader.exit_signals?.review > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"
                data-testid="health-strip-review-count"
              >
                🟡 {healthHeader.exit_signals.review} to review
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            Open the Health tab for full breakdown
          </span>
        </div>
      )}

      <Tabs defaultValue="holdings" className="w-full" data-testid="portfolio-dashboard-tabs">
        <TabsList className="grid w-full max-w-xl grid-cols-3" data-testid="portfolio-tabs-list">
          <TabsTrigger value="holdings" data-testid="tab-holdings">
            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Holdings
          </TabsTrigger>
          <TabsTrigger value="projection" data-testid="tab-projection">
            <Target className="h-3.5 w-3.5 mr-1.5" /> Return Projection
          </TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Health
          </TabsTrigger>
        </TabsList>

        <TabsContent value="holdings" className="mt-5 space-y-5">
          <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
        <Card className="border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="font-display">Holdings <span className="text-muted-foreground font-normal">({holdings.length})</span></CardTitle>
                {holdings.length > 0 && (
                  <div className="inline-flex items-center rounded-md border bg-muted/40 p-0.5" data-testid="holdings-view-toggle">
                    <Button
                      variant={holdingsView === "table" ? "secondary" : "ghost"}
                      size="sm"
                      className={`h-7 px-2 gap-1 text-xs ${holdingsView === "table" ? "shadow-sm" : ""}`}
                      onClick={() => setHoldingsView("table")}
                      data-testid="holdings-view-table"
                      aria-pressed={holdingsView === "table"}
                      title="Compact table view"
                    >
                      <TableIcon className="h-3.5 w-3.5" /> Table
                    </Button>
                    <Button
                      variant={holdingsView === "cards" ? "secondary" : "ghost"}
                      size="sm"
                      className={`h-7 px-2 gap-1 text-xs ${holdingsView === "cards" ? "shadow-sm" : ""}`}
                      onClick={() => setHoldingsView("cards")}
                      data-testid="holdings-view-cards"
                      aria-pressed={holdingsView === "cards"}
                      title="Detailed card view"
                    >
                      <LayoutGrid className="h-3.5 w-3.5" /> Cards
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onSync} data-testid="portfolio-sync-button">
                  Sync dividends
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportOpen(true)}
                  className="gap-1.5"
                  data-testid="portfolio-import-csv-button"
                  title="Import existing holdings from CSV"
                >
                  <FileText className="h-3.5 w-3.5" /> Import CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMonthlyOpen(true)}
                  className="gap-1.5"
                  data-testid="portfolio-monthly-invest-button"
                  title="Plan a monthly investment focused on underweight sectors"
                >
                  <TrendingUp className="h-3.5 w-3.5" /> + Monthly Investment
                </Button>
                <Button size="sm" onClick={onAdd} className="gap-2" data-testid="holding-add-button">
                  <Plus className="h-4 w-4" /> Add holding
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {holdings.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-background/60 p-6 text-center" data-testid="holdings-empty-state">
                <div className="font-display font-semibold">No holdings yet for this portfolio</div>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Click <span className="text-foreground font-medium">Add holding</span> when you make a real
                  purchase. Each holding is tracked with live price, gain/loss and dividends.
                </p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Button onClick={onAdd} className="gap-2" data-testid="empty-add-holding-button">
                    <Plus className="h-4 w-4" /> Add holding
                  </Button>
                </div>
              </div>
            ) : (
              holdingsView === "table" ? (
                <HoldingsTable holdings={holdings} onEdit={onEdit} onDelete={onDelete} />
              ) : (
                <div className="space-y-3">
                  {holdings.map((h) => (
                    <HoldingCard key={h.holding_id} h={h} onEdit={() => onEdit(h)} onDelete={() => onDelete(h)} />
                  ))}
                </div>
              )
            )}

            {remainingSuggestions.length > 0 && (
              <div className="rounded-lg border bg-accent/30 p-4" data-testid="empty-state-suggestion-preview">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-display font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> Claude’s suggested stocks for this portfolio
                    <Badge variant="secondary" className="ml-1 text-[10px]">{remainingSuggestions.length} to add</Badge>
                  </div>
                  {dismissed.length > 0 && (
                    <button
                      type="button"
                      onClick={onRestoreAllSuggestions}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="restore-suggestions-button"
                    >
                      <Undo2 className="h-3 w-3" /> Restore {dismissed.length} dismissed
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  These are AI recommendations based on your portfolio goals. When you actually purchase these
                  shares through your broker, click <span className="text-foreground font-medium">+ Add</span> to
                  record them and start tracking.
                </p>
                <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {remainingSuggestions.map((a, i) => (
                    <li
                      key={i}
                      data-testid={`suggested-stock-${a.cse_symbol}`}
                      className="flex items-center justify-between gap-2 rounded-md border bg-background p-2.5"
                    >
                      <div className="min-w-0">
                        <CompanyLink
                          companyCode={a.company_code}
                          name={a.company_name}
                          fromLabel="Portfolio"
                          className="text-sm font-medium truncate block"
                          testId={`suggested-name-${a.cse_symbol}`}
                        />
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                          <SymbolLink
                            companyCode={a.company_code}
                            symbol={a.cse_symbol}
                            fromLabel="Portfolio"
                            className="text-muted-foreground"
                            testId={`suggested-symbol-${a.cse_symbol}`}
                          />
                          <Badge variant="secondary" className="capitalize text-[10px] px-1.5 py-0">
                            {(a.share_type || "").replace("_", "-")}
                          </Badge>
                          {/* Phase 9 (Critical Bug 2 + colour fix) */}
                          {a.composite_signal && (
                            <SignalBadge signal={a.composite_signal} size="sm" />
                          )}
                          {(a.composite_score ?? a.score) != null && (
                            <ScoreText
                              score={a.composite_score ?? a.score}
                              className="text-[11px]"
                            />
                          )}
                          {a.data_confidence && (
                            <DataConfidenceDot
                              level={a.data_confidence}
                              annualReports={a.annual_reports || 0}
                              interimReports={a.interim_reports || 0}
                              companyCode={a.company_code}
                              size="sm"
                              testId={`suggested-conf-${a.cse_symbol}`}
                            />
                          )}
                          {a.sector_verdict && (
                            <SectorVerdictPill
                              verdict={a.sector_verdict}
                              size="sm"
                              testId={`suggested-verdict-${a.cse_symbol}`}
                            />
                          )}
                          <span>{a.shares} sh · ~{formatLKR(a.allocation_lkr, { compact: true, decimals: 1 })}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => onAddFromSuggestion(a)}
                          data-testid={`add-suggested-${a.cse_symbol}`}
                        >
                          <Plus className="h-3 w-3" /> Add
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Dismiss suggestion ${a.cse_symbol}`}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={() => onDismissSuggestion(a)}
                          data-testid={`dismiss-suggested-${a.cse_symbol}`}
                          title="Remove this suggestion"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {remainingSuggestions.length === 0 && dismissed.length > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                All suggestions dismissed.
                <button
                  type="button"
                  onClick={onRestoreAllSuggestions}
                  className="text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="restore-all-suggestions-button"
                >
                  <Undo2 className="h-3 w-3" /> Restore
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-5">
          <Card className="border bg-card">
            <CardHeader>
              <CardTitle className="font-display">Sector allocation</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: "100%", height: 280 }}>
                {sectorData.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Add holdings to see sector allocation.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280} key={`pie-${sectorData.length}`}>
                    <PieChart>
                      <Pie data={sectorData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2} isAnimationActive={false}>
                        {sectorData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(v, _n, item) => [`${v.toFixed(1)}%  (${formatLKR(item.payload.valueLkr)})`, item.payload.name]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-card">
            <CardHeader>
              <CardTitle className="font-display">Performers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <PerformerRow tone="up" label="Top performer" h={summary.top_performer} />
              <PerformerRow tone="down" label="Worst performer" h={summary.worst_performer} />
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="projection" className="mt-5 space-y-5">
          {holdings.length === 0 ? (
            <Card className="border bg-card">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Add at least one holding to see projected returns.
              </CardContent>
            </Card>
          ) : (
            <>
              <ReturnProjectionPanel
                portfolioId={summary.portfolio.portfolio_id}
                initialReinvest={summary.portfolio?.projection_settings?.reinvest_dividends || false}
                initialYears={summary.portfolio?.projection_settings?.holding_period_years || 5}
                testIdPrefix="dashboard-projection"
              />
              <ExpectedVsActualCard portfolioId={summary.portfolio.portfolio_id} />
            </>
          )}
        </TabsContent>

        <TabsContent value="health" className="mt-5">
          {holdings.length === 0 ? (
            <Card className="border bg-card">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Add at least one holding to see your portfolio health.
              </CardContent>
            </Card>
          ) : (
            <PortfolioHealthTab
              portfolioId={summary.portfolio.portfolio_id}
              suggestion={summary.portfolio?.ai_suggestion}
              onChange={onChange}
            />
          )}
        </TabsContent>
      </Tabs>

      <HoldingDialog
        open={dlgOpen}
        onClose={() => { setDlgOpen(false); setEditing(null); setPrefill(null); }}
        onSaved={() => {
          // If this dialog was opened via /portfolio?add=SYMBOL, redirect back
          // to the watchlist so the user can choose to remove it.
          const watchlistSym = fromWatchlistRef.current;
          fromWatchlistRef.current = null;
          closeAndRefresh();
          if (watchlistSym) {
            navigate(`/watchlist?just_purchased=${watchlistSym}`);
          }
        }}
        portfolioId={summary.portfolio.portfolio_id}
        editing={editing}
        prefill={prefill}
      />

      <SellHoldingDialog
        open={!!sellHolding}
        onClose={() => setSellHolding(null)}
        holding={sellHolding}
        onExited={async () => {
          setSellHolding(null);
          await onChange();
        }}
      />

      <MonthlyInvestmentDialog
        open={monthlyOpen}
        onClose={() => setMonthlyOpen(false)}
        portfolioId={summary.portfolio.portfolio_id}
        portfolioName={summary.portfolio.portfolio_name}
        onAdded={async () => {
          setMonthlyOpen(false);
          await onChange();
        }}
      />

      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        portfolioId={summary.portfolio.portfolio_id}
        onImported={async () => {
          setImportOpen(false);
          await onChange();
        }}
      />
    </div>
  );
};

const STATUS_STYLES = {
  strengthened: {
    badgeClass:
      "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    label: "Strengthened",
    description:
      "The investment thesis has improved since purchase — the signal upgraded and/or the composite score rose materially.",
  },
  stable: {
    badgeClass:
      "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    label: "Stable",
    description:
      "The investment thesis is roughly the same as when you purchased — signal unchanged with only minor score movement.",
  },
  weakened: {
    badgeClass:
      "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    label: "Weakened",
    description:
      "The investment thesis has deteriorated since purchase — the signal downgraded and/or the composite score fell materially.",
  },
};

const SignalStatusChip = ({ h }) => {
  const status = h.signal_status;
  const style = STATUS_STYLES[status];
  if (!status || status === "unknown" || !style) return null;

  const purchaseSignal = h.signal_at_purchase || "—";
  const purchaseScore =
    h.score_at_purchase != null ? Math.round(h.score_at_purchase) : "—";
  const currentSignal = h.composite_signal || "—";
  const currentScore =
    h.composite_score != null ? Math.round(h.composite_score) : "—";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${style.badgeClass}`}
          data-testid={`holding-signal-status-${h.holding_id}`}
          aria-label={`Signal status: ${style.label}`}
        >
          <span aria-hidden="true">{h.signal_status_icon}</span>
          <span>{style.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[300px] p-0 overflow-hidden"
        data-testid={`holding-signal-status-popover-${h.holding_id}`}
      >
        <div className="px-3 pt-3 pb-2 border-b bg-muted/20">
          <div className="text-sm font-display font-semibold leading-tight">
            Investment thesis: {style.label}
          </div>
          {h.signal_status_tooltip && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {h.signal_status_tooltip}
            </div>
          )}
        </div>
        <div className="px-3 py-2.5 space-y-2 text-xs">
          <p className="text-muted-foreground leading-snug">{style.description}</p>
          {h.snapshot_backfilled && (
            <p className="text-[11px] italic text-muted-foreground">
              ⓘ Snapshot estimated from current data (purchased before this feature shipped).
            </p>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="rounded-md border bg-background/60 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                At purchase
              </div>
              <div className="mt-0.5 text-sm font-semibold tabular">
                {purchaseSignal}{" "}
                <span className="text-muted-foreground font-normal">
                  · {purchaseScore}/100
                </span>
              </div>
            </div>
            <div className="rounded-md border bg-background/60 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Now
              </div>
              <div className="mt-0.5 text-sm font-semibold tabular">
                {currentSignal}{" "}
                <span className="text-muted-foreground font-normal">
                  · {currentScore}/100
                </span>
              </div>
            </div>
          </div>
          {h.purchase_rationale_snapshot && (
            <div className="pt-1.5 border-t mt-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Snapshot rationale
              </div>
              <p className="text-[12px] leading-snug text-foreground/90">
                {h.purchase_rationale_snapshot}
              </p>
            </div>
          )}
        </div>
        {h.company_code && (
          <div className="border-t px-3 py-2 bg-muted/20">
            <Link
              to={`/stocks/${h.company_code}`}
              state={{ from: { pathname: "/portfolio", label: "Portfolio" } }}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`holding-signal-status-link-${h.holding_id}`}
            >
              View full analysis <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// Medium #14 — Compact Holdings Table view (default)
// ---------------------------------------------------------------------------
const SIGNAL_TABLE_CLASS = {
  BUY: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  WATCH: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  HOLD: "bg-muted text-muted-foreground border-border",
  SELL: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
  AVOID: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const HoldingsTable = ({ holdings, onEdit, onDelete }) => {
  return (
    <div className="rounded-lg border overflow-hidden" data-testid="holdings-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Company</th>
              <th className="px-3 py-2 text-right font-medium">Shares</th>
              <th className="px-3 py-2 text-right font-medium">Avg Cost</th>
              <th className="px-3 py-2 text-right font-medium">Current</th>
              <th className="px-3 py-2 text-right font-medium">Value</th>
              <th className="px-3 py-2 text-right font-medium">P/L %</th>
              <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Yield</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Conf</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Sector</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Valuation</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Signal</th>
              <th className="px-3 py-2 text-right font-medium w-[80px]"></th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h) => {
              const up = (h.gain_loss_pct ?? 0) >= 0;
              const changeClass = up ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]";
              const signal = (h.adjusted_signal || h.composite_signal || "").toUpperCase();
              const yieldPct = h.current_yield_pct ?? h.dividend_yield_pct ?? null;
              return (
                <tr
                  key={h.holding_id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                  data-testid={`holding-row-${h.holding_id}`}
                >
                  <td className="px-3 py-2 align-middle">
                    <CompanyLink
                      companyCode={h.company_code}
                      name={h.company_name}
                      fromLabel="Portfolio"
                      className="font-medium text-sm"
                      testId={`holding-row-name-${h.holding_id}`}
                    />
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {h.cse_symbol}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{h.shares_purchased}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatLKR(h.purchase_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatLKR(h.current_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatLKR(h.current_value)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-medium ${changeClass}`}>
                    {h.gain_loss_pct != null ? formatPct(h.gain_loss_pct) : "—"}
                    <div className={`text-[10px] ${changeClass}`}>
                      {h.gain_loss_lkr != null ? `${(h.gain_loss_lkr ?? 0) >= 0 ? "+" : ""}${formatLKR(h.gain_loss_lkr, { decimals: 0 })}` : ""}
                    </div>
                  </td>                  <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">
                    {yieldPct != null ? formatPct(yieldPct) : "—"}
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    {h.data_confidence ? (
                      <DataConfidenceDot
                        level={h.data_confidence}
                        annualReports={h.annual_reports || 0}
                        interimReports={h.interim_reports || 0}
                        companyCode={h.company_code}
                        size="sm"
                        testId={`holding-row-conf-${h.holding_id}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    {h.sector_verdict ? (
                      <SectorVerdictPill
                        verdict={h.sector_verdict}
                        size="sm"
                        testId={`holding-row-verdict-${h.holding_id}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  {/* Valuation Status (2026-05-03) — same pill as screener. */}
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    <ValuationBadge
                      status={h.valuation_status}
                      discountPct={h.valuation_discount_pct}
                      size="sm"
                      short
                      className={`holding-row-valuation-${h.holding_id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    {signal ? (
                      <SignalBadge
                        signal={signal}
                        size="sm"
                        className={`holding-row-signal-${h.holding_id}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onEdit(h)}
                        aria-label="Edit"
                        data-testid={`holding-row-edit-${h.holding_id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                        onClick={() => onDelete(h)}
                        aria-label="Delete"
                        data-testid={`holding-row-delete-${h.holding_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const HoldingCard = ({ h, onEdit, onDelete }) => {
  const up = (h.gain_loss_pct ?? 0) >= 0;
  return (
    <div data-testid={`holding-card-${h.holding_id}`} className="rounded-lg border bg-background p-4 hover:bg-muted/30 transition-colors duration-150">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <CompanyLink
            companyCode={h.company_code}
            name={h.company_name}
            fromLabel="Portfolio"
            className="font-medium block"
            testId={`holding-name-${h.holding_id}`}
          />
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <SymbolLink
              companyCode={h.company_code}
              symbol={h.cse_symbol}
              fromLabel="Portfolio"
              className="text-muted-foreground"
              testId={`holding-symbol-${h.holding_id}`}
            />
            <Badge variant="secondary" className="capitalize text-[10px] px-1.5 py-0">
              {(h.share_type || "").replace("_", "-")}
            </Badge>
            <span>{h.sector}</span>
            {/* Phase 9 fixes — Holdings card uses the same SignalBadge,
                ScoreText, DataConfidenceDot and SectorVerdictPill as
                everywhere else for visual consistency. */}
            {(h.composite_signal || h.adjusted_signal) && (
              <SignalBadge
                signal={h.adjusted_signal || h.composite_signal}
                size="sm"
                className=""
              />
            )}
            {(h.composite_score ?? h.adjusted_score) != null && (
              <ScoreText
                score={h.adjusted_score ?? h.composite_score}
                className="text-[11px]"
              />
            )}
            {h.data_confidence && (
              <DataConfidenceDot
                level={h.data_confidence}
                annualReports={h.annual_reports || 0}
                interimReports={h.interim_reports || 0}
                companyCode={h.company_code}
                size="sm"
                testId={`holding-conf-${h.holding_id}`}
              />
            )}
            {h.sector_verdict && (
              <SectorVerdictPill
                verdict={h.sector_verdict}
                size="sm"
                testId={`holding-verdict-${h.holding_id}`}
              />
            )}
            <SignalStatusChip h={h} />
            {h.is_investment_trust && (
              <Badge variant="outline" className="text-[10px] gap-0.5 px-1.5 py-0 border-info/40 text-info">
                <Building2 className="h-2.5 w-2.5" /> Trust
              </Badge>
            )}
            {h.special_dividend_detected && (
              <Badge variant="outline" className="text-[10px] gap-0.5 px-1.5 py-0 border-warning/40 text-warning">
                <AlertTriangle className="h-2.5 w-2.5" /> Special div
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit" data-testid={`holding-edit-${h.holding_id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Delete" data-testid={`holding-delete-${h.holding_id}`} className="text-destructive hover:bg-destructive/10">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Cell2 label="Shares" value={h.shares_purchased} />
        <Cell2
          label="Avg cost / share"
          value={formatLKR(h.purchase_price)}
          helpTermId="average-cost-basis"
        />
        <Cell2 label="Current price" value={formatLKR(h.current_price)} />
        <Cell2
          label="Total invested"
          value={formatLKR(h.total_cost_inc_charges || h.cost_basis)}
          hint={
            h.brokerage_buy
              ? `${formatLKR(h.cost_basis, { decimals: 0 })} + ${formatLKR(h.brokerage_buy, { decimals: 0 })} brokerage`
              : null
          }
        />
        <Cell2 label="Current value" value={formatLKR(h.current_value)} />
        <Cell2
          label="Unrealised P/L"
          helpTermId="unrealised-gain-loss"
          value={
            <span className={up ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}>
              {(h.gain_loss_lkr ?? 0) >= 0 ? "+" : ""}{formatLKR(h.gain_loss_lkr)}
              <span className="ml-1 text-xs">({formatPct(h.gain_loss_pct)})</span>
            </span>
          }
          hint={
            h.brokerage_buy ? "after buying brokerage" : null
          }
        />
        <Cell2
          label="Dividends"
          helpTermId="dividend-wht"
          value={formatLKR(h.dividend_income_lkr)}
          hint={
            h.dividend_income_lkr_gross
              ? `Net of 10% WHT · ${h.receipts_count} payment${h.receipts_count === 1 ? "" : "s"}`
              : `${h.receipts_count} payment${h.receipts_count === 1 ? "" : "s"}`
          }
        />
        <Cell2
          label="Yield on cost"
          helpTermId="yield-on-cost"
          value={
            <span>
              <span className="text-[hsl(var(--success))]">{formatPct(h.yield_on_cost_pct)}</span>
              <span className="ml-1 text-xs text-muted-foreground">vs current {formatPct(h.current_yield_pct)}</span>
            </span>
          }
          hint="net of 10% WHT"
        />
      </div>

      {/* Stage 1 — breakeven + tax status chips */}
      <BrokerageChips h={h} />
      <div className="mt-3 text-xs text-muted-foreground">
        Purchased {formatDate(h.purchase_date)}
        {(h.tranche_count || 0) > 1 && (
          <>
            {" · "}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="underline decoration-dotted hover:text-foreground"
                  data-testid={`holding-tranches-${h.holding_id}`}
                >
                  {h.tranche_count} purchases · Avg cost{" "}
                  {formatLKR(h.average_cost, { decimals: 2 })}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0">
                <div className="px-3 pt-2.5 pb-1.5 border-b">
                  <div className="font-display text-sm font-semibold">Purchase history</div>
                </div>
                <div className="px-3 py-2 space-y-1 text-xs">
                  {(h.purchase_tranches || []).map((t, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-2 py-0.5"
                      data-testid={`tranche-${h.holding_id}-${i}`}
                    >
                      <span className="text-muted-foreground">{formatDate(t.date)}</span>
                      <span className="tabular">
                        {Number(t.shares).toLocaleString()} sh @{" "}
                        {formatLKR(t.price, { decimals: 2 })}
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-1.5 mt-1 space-y-0.5">
                    <div className="flex justify-between font-medium">
                      <span>Total shares</span>
                      <span className="tabular">
                        {Number(h.total_shares || h.shares_purchased).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Avg cost</span>
                      <span className="tabular">
                        {formatLKR(h.average_cost, { decimals: 2 })}/share
                      </span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Total invested</span>
                      <span className="tabular">{formatLKR(h.total_invested)}</span>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
        {h.notes ? ` · ${h.notes}` : ""}
      </div>

      {/* Phase 7 Stage 7 — projection chips */}
      {(h.projected_annual_income != null || h.projection_target_price != null) && (
        <div
          className="mt-2.5 flex flex-wrap items-center gap-1.5"
          data-testid={`holding-projection-chips-${h.holding_id}`}
        >
          {h.projected_annual_income != null && h.projected_annual_income > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] gap-1 px-1.5 py-0.5 border-primary/30 bg-primary/5 text-primary"
              data-testid={`holding-projection-income-${h.holding_id}`}
              title="Projected base-case annual dividend income"
            >
              <Coins className="h-2.5 w-2.5" />
              Est. {formatLKR(h.projected_annual_income, { compact: true, decimals: 1 })}/yr
            </Badge>
          )}
          {h.projection_target_price != null && h.current_price != null && (
            (() => {
              const upside = h.current_price > 0
                ? ((h.projection_target_price - h.current_price) / h.current_price) * 100
                : 0;
              const positive = upside >= 0;
              return (
                <Badge
                  variant="outline"
                  className={`text-[10px] gap-1 px-1.5 py-0.5 ${
                    positive
                      ? "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 text-[hsl(var(--success))]"
                      : "border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 text-[hsl(var(--destructive))]"
                  }`}
                  data-testid={`holding-projection-target-${h.holding_id}`}
                  title="Base-case projected target price"
                >
                  <Target className="h-2.5 w-2.5" />
                  Target {formatLKR(h.projection_target_price, { compact: false, decimals: 2 })}
                  <span className="ml-0.5">
                    ({positive ? "+" : ""}
                    {upside.toFixed(1)}%)
                  </span>
                </Badge>
              );
            })()
          )}
        </div>
      )}
      {/* Phase 7 Stage 8 — exit signal banner */}
      {h.exit_signal_status === "strong" && (
        <div
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-xs"
          data-testid={`holding-exit-banner-strong-${h.holding_id}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--destructive))]" />
            <div className="flex-1">
              <div className="font-display font-semibold text-[hsl(var(--destructive))]">
                🔴 Exit Signal Detected
              </div>
              {h.exit_signal_reason && (
                <p className="text-muted-foreground mt-0.5 leading-snug">{h.exit_signal_reason}</p>
              )}
              <p className="text-muted-foreground mt-0.5 leading-snug">
                Consider reviewing this position.
              </p>
            </div>
            <Link
              to={`/stocks/${h.company_code}`}
              state={{ from: { pathname: "/portfolio", label: "Portfolio" } }}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 shrink-0"
              data-testid={`holding-exit-view-${h.holding_id}`}
            >
              View Analysis <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
      {h.exit_signal_status === "review" && (
        <div
          className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-2.5 text-xs"
          data-testid={`holding-exit-banner-review-${h.holding_id}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
            <div className="flex-1">
              <div className="font-display font-semibold text-[hsl(var(--warning))]">
                🟡 Review Recommended
              </div>
              {h.exit_signal_reason && (
                <p className="text-muted-foreground mt-0.5 leading-snug">{h.exit_signal_reason}</p>
              )}
            </div>
            <Link
              to={`/stocks/${h.company_code}`}
              state={{ from: { pathname: "/portfolio", label: "Portfolio" } }}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 shrink-0"
            >
              View Analysis <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const Cell2 = ({ label, value, hint, helpTermId }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground inline-flex items-center">
      {label}
      {helpTermId && <HelpTooltip termId={helpTermId} size="xs" />}
    </div>
    <div className="mt-0.5 tabular font-medium">{value ?? "—"}</div>
    {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

// ----------------------------------------------------------------------
// Stage 1 — Breakeven chip + retroactive flag for HoldingCard.
// (Capital gains on CSE listed shares are tax-free in Sri Lanka, so
//  no tax-status chip is shown here. Dividends are net of 10% WHT.)
// ----------------------------------------------------------------------
const BrokerageChips = ({ h }) => {
  if (!h) return null;
  const breakevenPrice = h.breakeven_price;
  const breakevenStatus = h.breakeven_status;
  if (!breakevenPrice && h.brokerage_source !== "retroactive") return null;

  const beAbove = breakevenStatus === "above";

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`holding-brokerage-chips-${h.holding_id}`}>
      {breakevenPrice ? (
        <Badge
          variant="outline"
          className={`text-[11px] gap-1 px-2 py-0.5 ${
            beAbove
              ? "border-success/40 text-success bg-success/10"
              : "border-warning/40 text-warning bg-warning/10"
          }`}
          title="Price needed to break even after both legs of brokerage"
          data-testid={`breakeven-chip-${h.holding_id}`}
        >
          Breakeven {formatLKR(breakevenPrice, { decimals: 2 })}
          {h.current_price ? (
            <span className="opacity-80">
              · Now {formatLKR(h.current_price, { decimals: 2 })}
              {beAbove ? " ✓" : ""}
            </span>
          ) : null}
        </Badge>
      ) : null}
      {h.brokerage_source === "retroactive" && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground border-dashed" title="Brokerage on this holding was estimated retroactively at the current rate.">
          est. brokerage
        </Badge>
      )}
    </div>
  );
};

const PerformerRow = ({ tone, label, h }) => {
  if (!h) return <div className="text-sm text-muted-foreground">{label}: —</div>;
  const Icon = tone === "up" ? TrendingUp : TrendingDown;
  const cls = tone === "up" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]";
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <CompanyLink
          companyCode={h.company_code}
          name={h.company_name}
          fromLabel="Portfolio"
          className="font-medium truncate block"
        />
        <SymbolLink
          companyCode={h.company_code}
          symbol={h.cse_symbol}
          fromLabel="Portfolio"
          className="text-muted-foreground"
        />
      </div>
      <div className={`flex items-center gap-1 ${cls} tabular`}>
        <Icon className="h-4 w-4" />
        {formatPct(h.gain_loss_pct)}
      </div>
    </div>
  );
};

export default PortfolioDashboard;
